// Búsqueda de órdenes contra Typesense, con la misma entrada (appliedFilters
// de OrdersPage2) y la misma salida ({ orders, totalItems, totalPages }) que
// /orders/list, para comparar las dos fuentes en la misma tabla.
//
// La colección la llena search-sync (src/orders): un documento por orden con
// direcciones, productos, pagos, envíos y devoluciones. Su alias es el código
// de la tienda con orders_ en vez de nkr_ (nkr_usa -> orders_usa).
//
// Las órdenes llevan datos personales y Typesense sólo admite a los servidores:
// el navegador pide a la API de la tienda (Order_search en admin-store-api), que
// busca limitado al departamento y, sin permiso para ver las de otros vendedores,
// a las del usuario.

import httpStore, { getSelectedDepartmentCode } from "@/api/httpStore.js";
import { vinCheckDigitOk } from "@/utils/vinDecode";

// department por defecto, el seleccionado; "ALL" = toda la tienda. El resultado
// viaja como string para que httpStore no camelice las claves de los documentos.
const runSearch = async (
  search,
  department = getSelectedDepartmentCode() || "ALL"
) => {
  const { data } = await httpStore.post(
    "/order_search/orders",
    { search },
    { params: { department } }
  );
  return JSON.parse(data.resultJson);
};

/**
 * Si esta tienda tiene búsqueda de órdenes en Typesense: la API responde 404 si
 * le falta el setting typesense_orders_key.
 */
export const checkTypesenseOrdersAvailable = async () => {
  try {
    await httpStore.get("/order_search/available");
    return true;
  } catch (e) {
    return false;
  }
};

// Orden de prioridad (SEARCHABLE_FIELDS en search-sync/src/orders/schema.js).
const QUERY_FIELDS = [
  "order_number",
  "purchase_order_number",
  "vin",
  "product_codes",
  "product_codes_glued",
  "payment_codes",
  "tracking_numbers",
  "rmas",
  "email",
  "customer_name",
  "ship_name",
  "bill_name",
  "ship_company",
  "bill_company",
  "phones",
  "phones_digits",
  "postal_codes",
  "ship_address",
  "bill_address",
  "payment_holders",
  "product_descriptions",
  "discount_coupon"
];

// Campos donde un trozo del medio tiene que encontrar el valor ("74486" ->
// 100074486). Declarados con infix en el schema.
const INFIX_FIELDS = new Set([
  "order_number",
  "purchase_order_number",
  "product_codes",
  "payment_codes",
  "tracking_numbers"
]);

const SORT_FIELDS = {
  orderDate: "date",
  orderNumber: "order_number_int",
  total: "total"
};

// Los valores del menú lateral, como los traduce Order_model::translateOrderStatus
// (más los pseudo-estados de dropship de Orders::listFiltersFromRequest).
// Claves normalizadas con statusKey.
const STATUS_FILTERS = {
  cancelled: { status: "Cancelled" },
  shipped: { status: "Shipped" },
  awaiting_shipment: { status: "Awaiting Shipment" },
  on_hold: { status: "On Hold" },
  awaiting_payment: { status: "Awaiting Payment" },
  draft: { status: "Draft" },
  vin_verification: { status: "VIN verification" },
  cancel_request_pending: { status: "Cancel request pending" },
  dropship: { status: "Dropship" },
  dropship_processed: { status: "Dropship", dropshipProcessed: 1 },
  dropship_pending: { status: "Dropship", dropshipProcessed: 0 },
  local_dropship: { status: "Local Dropship", dropshipProcessed: 0 }
};

// El menú manda unos estados con espacios y mayúsculas ("Dropship processed",
// "Dropship Pending", "Local Dropship"); /orders/list los recibe con "_" y el
// backend los compara en minúsculas. Aquí igual: si no, no casan con
// STATUS_FILTERS y se filtraban como status literal, que no existe.
const statusKey = value =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .toLowerCase();

const quote = value => `\`${String(value).replace(/`/g, "")}\``;

// Las fechas del índice son la hora de pared de orders_v2 guardada como UTC
// (ver toUnix en search-sync/src/orders/documents.js): se leen y se filtran en
// UTC para ver exactamente lo que hay en la base, esté donde esté el navegador.
const dayToUnix = (day, endOfDay = false) => {
  const date = new Date(`${day}T${endOfDay ? "23:59:59" : "00:00:00"}Z`);
  const ms = date.getTime();
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
};

// Por qué no todo con los mismos parámetros:
// - Un código (lleva dígitos): sin tolerancia a errores, que para números
//   sólo mete ruido ("74486" encontraría 14486).
// - Un código de UN solo token: además infix, para encontrar números a medias.
//   Con separadores no: "990-1207" se parte en 990 y 1207, y cada trozo
//   aparece dentro de casi cualquier número.
const searchParams = query => {
  const isCode = /\d/.test(query);
  const singleToken = /^[A-Za-z0-9]+$/.test(query);
  return {
    num_typos: isCode ? 0 : 2,
    infix: QUERY_FIELDS.map(field =>
      isCode && singleToken && INFIX_FIELDS.has(field) ? "always" : "off"
    ).join(",")
  };
};

// Un importe con decimales ("161.87", "$161,87", "1,161.87") -> 1161.87. null
// si no lo es: un entero ("154") puede ser un número de orden o un código.
const parseAmount = token => {
  const text = token.replace(/\$/g, "");
  const match = /^(\d{1,3}(?:[.,]\d{3})+|\d+)[.,](\d{1,2})$/.exec(text);
  if (!match) return null;
  return Number(`${match[1].replace(/[.,]/g, "")}.${match[2]}`);
};

// Separa de lo escrito los importes, que se filtran por el campo total (una
// orden con ese total exacto), del resto, que se busca como texto:
// "escalante 56.44" = órdenes de escalante cuyo total es 56.44. Buscar el
// importe como texto no sirve: sin el punto, 161.87 coincide con la orden 16187.
const splitAmounts = query => {
  const amounts = [];
  const words = [];
  query
    .split(/\s+/)
    .filter(Boolean)
    .forEach(word => {
      const amount = parseAmount(word);
      if (amount === null) words.push(word);
      else amounts.push(amount);
    });
  return { text: words.join(" "), amounts };
};

// Medio centavo de margen: total es un float y 161.87 puede estar guardado
// como 161.869999.
const amountFilter = amounts =>
  amounts.length
    ? `total:[${amounts
        .map(a => `${(a - 0.005).toFixed(3)}..${(a + 0.005).toFixed(3)}`)
        .join(",")}]`
    : "";

// Los filtros del panel vienen dentro de otherFilters (OrdersPage2); a
// /orders/list llegan aplanados por createQueryString, así que aquí también.
const buildFilter = (appliedFilters, departmentCode) => {
  const filters = { ...appliedFilters, ...(appliedFilters.otherFilters || {}) };
  const parts = [];
  const status = STATUS_FILTERS[statusKey(filters.statusShipment)];
  if (status) {
    parts.push(`status:=${quote(status.status)}`);
    // Los pseudo-estados fijan dropship_processed; con "dropship" lo decide el
    // selector Pending/Processed, como en Order_model::getOrdersFromIndex.
    const processed =
      status.dropshipProcessed !== undefined
        ? status.dropshipProcessed
        : status.status === "Dropship"
        ? filters.dropshipProcessed
        : undefined;
    if (processed !== undefined && processed !== null && processed !== "") {
      parts.push(`dropship_processed:=${Number(processed)}`);
    }
  } else if (filters.statusShipment) {
    parts.push(`status:=${quote(filters.statusShipment)}`);
  }
  if (filters.hideCancelled) parts.push("status:!=`Cancelled`");
  if (filters.hideDraft) parts.push("status:!=`Draft`");
  if (filters.fromDate) {
    const from = dayToUnix(filters.fromDate);
    if (from !== null) parts.push(`date:>=${from}`);
  }
  if (filters.toDate) {
    const to = dayToUnix(String(filters.toDate).slice(0, 10), true);
    if (to !== null) parts.push(`date:<=${to}`);
  }
  if (filters.createdBy) parts.push(`created_by:=${quote(filters.createdBy)}`);
  if (filters.seller) parts.push(`seller:=${quote(filters.seller)}`);
  if (filters.paidStatus !== "" && filters.paidStatus != null) {
    parts.push(`status_payment:=${Number(filters.paidStatus)}`);
  }
  if (filters.creditRetail !== "" && filters.creditRetail != null) {
    parts.push(`credit_retail:=${quote(filters.creditRetail)}`);
  }
  if (filters.customerInfoautoId) {
    parts.push(`customer_infoauto_id:=${quote(filters.customerInfoautoId)}`);
  }
  if (filters.totalMin) parts.push(`total:>=${Number(filters.totalMin)}`);
  if (filters.totalMax) parts.push(`total:<=${Number(filters.totalMax)}`);
  // ':' (no ':=') en campos de texto: coincide por palabras, como el LIKE.
  if (filters.nkrCode) parts.push(`product_codes:${quote(filters.nkrCode)}`);
  if (filters.shippingAddressName) {
    parts.push(`ship_name:${quote(filters.shippingAddressName)}`);
  }
  if (departmentCode && departmentCode !== "ALL") {
    parts.push(`department:=${quote(departmentCode)}`);
  }
  return parts.join(" && ");
};

const pad = n => String(n).padStart(2, "0");
const unixToDateTime = seconds => {
  if (!seconds) return null;
  const d = new Date(seconds * 1000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
    d.getUTCDate()
  )} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(
    d.getUTCSeconds()
  )}`;
};

// Documento -> fila con los nombres de list_metadata que pinta OrdersPage2.
const toRow = doc => ({
  id: doc.order_number,
  orderNumber: doc.order_number,
  parentOrderNumber: doc.parent_order_number || null,
  orderDate: unixToDateTime(doc.date),
  status: doc.status || null,
  total: doc.total,
  products: (doc.product_codes || []).join(","),
  shippingAddressName: doc.ship_name || null,
  shippingAddressState: doc.ship_state || null,
  shippingAddressCountry: doc.ship_country || null,
  createdBy: doc.created_by || null,
  shippingPreference: doc.shipping_preference || null,
  salesman: doc.seller || null,
  sellerName: doc.seller_name || null,
  vin: doc.vin || null,
  statusPayment: doc.status_payment,
  dropshipProcessed: doc.dropship_processed
});

/**
 * Misma firma de resultado que getOrdersFromIndex, más searchTimeMs para
 * comparar con MySQL.
 */
export const searchOrders = async ({ filters, departmentCode }) => {
  const query = String(filters.search || "").trim();
  // Typesense sirve como mucho 250 por página.
  const perPage = Math.min(Number(filters.limitPerPage) || 20, 250);
  const page = Number(filters.page) || 1;
  const sortField = SORT_FIELDS[filters.orderCol] || "date";
  const sortDir = filters.orderDir === "asc" ? "asc" : "desc";

  const { text, amounts } = splitAmounts(query);
  const filterBy = [buildFilter(filters, departmentCode), amountFilter(amounts)]
    .filter(Boolean)
    .join(" && ");

  const search = {
    q: text || "*",
    query_by: QUERY_FIELDS.join(","),
    ...(text ? searchParams(text) : {}),
    filter_by: filterBy,
    // Con texto, primero lo que mejor coincide; el orden elegido desempata.
    sort_by: text
      ? `_text_match:desc,${sortField}:${sortDir}`
      : `${sortField}:${sortDir}`,
    page,
    per_page: perPage,
    // Sólo lo que pinta la fila: el documento completo pesa mucho más.
    include_fields: [
      "order_number",
      "parent_order_number",
      "date",
      "status",
      "total",
      "product_codes",
      "ship_name",
      "ship_state",
      "ship_country",
      "created_by",
      "shipping_preference",
      "seller",
      "seller_name",
      "vin",
      "status_payment",
      "dropship_processed"
    ].join(",")
  };

  const result = await runSearch(search);
  const totalItems = result.found || 0;
  return {
    orders: (result.hits || []).map(hit => toRow(hit.document)),
    totalItems,
    totalPages: totalItems > 0 ? Math.ceil(totalItems / perPage) : 1,
    searchTimeMs: result.search_time_ms
  };
};

/**
 * Los VIN completos de nuestras órdenes que terminan en `tail` (los últimos 8
 * que suelen dar los clientes de camiones), el más fiable primero:
 * [{ vin, orders, lastOrder, checkOk }].
 *
 * Busca en toda la tienda, no sólo en el departamento seleccionado: Miami y
 * Pompano comparten clientes. Varios VIN con el mismo final casi siempre son
 * el mismo camión mal escrito (JALC4814… y JALC4B14…); va primero el que
 * cuadra con el dígito de control y, entre esos, el de más órdenes.
 * Necesita `infix` en `vin` (search-sync/src/orders/schema.js).
 */
export const findVinsByTail = async tail => {
  const search = {
    q: tail,
    query_by: "vin",
    infix: "always",
    num_typos: 0,
    per_page: 100,
    sort_by: "date:desc",
    include_fields: "vin,order_number"
  };
  const result = await runSearch(search, "ALL");

  // Sólo VIN completos: hay órdenes que guardaron únicamente los 8 finales.
  const byVin = new Map();
  (result.hits || []).forEach(({ document }) => {
    const vin = String(document.vin || "")
      .replace(/\s+/g, "")
      .toUpperCase();
    if (vin.length !== 17 || !vin.endsWith(tail)) return;
    const found = byVin.get(vin);
    if (found) {
      found.orders += 1;
    } else {
      // Van de la más reciente a la más antigua: la primera es la última.
      byVin.set(vin, {
        vin,
        orders: 1,
        lastOrder: document.order_number,
        checkOk: vinCheckDigitOk(vin)
      });
    }
  });
  return [...byVin.values()].sort(
    (a, b) => b.checkOk - a.checkOk || b.orders - a.orders
  );
};
